// IE 2026 09 08 / ODOO-… (заказ Игоря): поведение СВОБОДНЫХ (popout) виджетов, когда активным становится
// ДОКИРОВАННЫЙ, который они перекрывают. Срабатывает на ЛЮБУЮ активацию — клик по области виджета,
// Alt+Tab, меню, widget_open: точечный watch на store.activeWIDGET.id (+ ресайз окна, смена страницы,
// конец перетаскивания). Активен свободный виджет или режим 'stack' — обычная раскладка.
//
// Режимы (меню Пользователь → Widgets → Behavior; настройка fe.ux.freeWidgets, по умолчанию 'stack' —
// уточнение Игоря 08.09):
//   'stack'   — как было (по умолчанию): свободные всегда поверх докированных, активный докированный остаётся под ними;
//   'front'   — A: активный виджет всегда сверху — докированный, став активным, поднимается
//               над свободными (атрибут data-fw-front → z 1300: выше свободных 900+, ниже wijmo-попапов 1500);
//               свободные остаются на месте, там, где перекрыты, их не видно;
//   'ghost'   — B: свободные, перекрывающие активный докированный, полупрозрачны и прозрачны для кликов
//               (клик уходит в докированный), шапка живая: наведение — показать целиком (peek), клик — вернуть;
//   'outline' — C: те же свободные — контур (рамка цвета гаммы + шапка), тело невидимо и не ловит клики;
//   'shade'   — D: те же свободные свёрнуты до строки шапки на своём месте (--fw-shade-h), наведение раскрывает;
//   'park'    — E: те же свободные скрыты и представлены чипами в полосе у нижнего края (FreeWidgetsStrip.vue);
//               наведение на чип — показать, клик — вернуть.
// Визуал — атрибуты на контейнере #WIDGET<id> (data-fw="<режим>", data-fw-peek, data-fw-front) и CSS в
// FreeWidgetsStrip.vue. Именно атрибуты, не классы: className контейнера переписывает реактивный binding
// Widget.vue при каждой активации. Переключение без пересборки: window.feSettings.set('fe.ux.freeWidgets', '<режим>').
import { reactive, markRaw, watch } from 'vue'
import { t } from '@/i18n'   // IE 2026 09 23 / ODOO-341407: i18n

// short — подпись чипа в полосе Behavior листа Alt+Tab; strip — показывать в полосе (уточнение Игоря 08.09:
// только «Free widgets always in front» и «Park»; остальные режимы — в меню Пользователь → Widgets → Behavior)
export const FW_MODES = [   // IE 2026 09 23 / ODOO-341407: i18n — подписи геттерами: словарь грузится после импорта модуля, t() выполняется при чтении
    { id: 'stack',   get title() { return t('Free widgets always in front') },             get short() { return t('Free widgets in front') }, strip: true },
    { id: 'front',   get title() { return t('Active widget in front') },                   get short() { return t('Active in front') } },
    { id: 'ghost',   get title() { return t('Ghost free widgets over the active one') },   get short() { return t('Ghost') } },
    { id: 'outline', get title() { return t('Outline free widgets over the active one') }, get short() { return t('Outline') } },
    { id: 'shade',   get title() { return t('Shade free widgets to title bars') },         get short() { return t('Shade') } },
    { id: 'park',    get title() { return t('Park free widgets to the edge') },            get short() { return t('Park free widgets') }, strip: true },
]
export const FW_DEFAULT = 'stack'

const freeWidgets = {
    state: reactive({
        mode: FW_DEFAULT,   // зеркало настройки — для меню и полосы парковки
        parked: [],         // [{id, title, pi, W}] — свободные виджеты, убранные в полосу (режим 'park')
    }),
    store: null,
    stats: { applies: 0, marks: 0 },
    _installed: false,
    _front: null,       // поднятый докированный контейнер (data-fw-front)
    _marked: [],        // контейнеры свободных с data-fw
    _peekEl: null,      // контейнер, показанный целиком по наведению (data-fw-peek)
    _peekTimer: null,
    _applyTimer: null,

    mode() {
        try {
            const m = this.store?.setting?.('fe.ux.freeWidgets', FW_DEFAULT)
            return FW_MODES.some(x => x.id === m) ? m : FW_DEFAULT
        } catch (e) { return FW_DEFAULT }
    },
    setMode(id) {
        if (!FW_MODES.some(x => x.id === id)) return
        // override этого браузера (localStorage 'feSettings') + _settingsEpoch++ — меню перечитает галочки
        if (window.feSettings?.set) window.feSettings.set('fe.ux.freeWidgets', id)
        this.state.mode = id
        this.apply()
    },
    // пункты меню Пользователь → Widgets → Behavior (галочка у текущего режима, кружок у остальных)
    menuItems() {
        const cur = this.mode()
        return FW_MODES.map(m => ({
            title: m.id === FW_DEFAULT ? t('{title} (default)', { title: m.title }) : m.title,   // IE 2026 09 23 / ODOO-341407: i18n
            cssClass: cur === m.id ? 'pi pi-check' : 'pi pi-circle',
            command: 'freeWidgetsMode',
            mode: m.id,
        }))
    },

    install(store) {
        this.store = store
        if (this._installed) return
        this._installed = true
        this.state.mode = this.mode()
        // точечные watch (deep-watch стора читает wijmo-геттеры виджетов — нельзя)
        this._stops = [
            watch(() => store.activeWIDGET?.id, () => this.schedule()),
            watch(() => store.activeWORKSPACE?.activePAGE?.page ?? store.activeWORKSPACE?.active_page, () => this.schedule()),
            watch(() => store._settingsEpoch, () => { this.state.mode = this.mode(); this.schedule() }),
            watch(() => store.settings_version, () => { this.state.mode = this.mode(); this.schedule() }),
        ]
        this._onResize = () => this.schedule(80)
        this._onPointerUp = () => this.schedule(120)   // конец перетаскивания/ресайза свободного виджета — пересечения могли измениться
        this._onOver = (e) => this.onOver(e)
        this._onOut = (e) => this.onOut(e)
        window.addEventListener('resize', this._onResize)
        document.addEventListener('pointerup', this._onPointerUp, true)
        document.addEventListener('mouseover', this._onOver, true)
        document.addEventListener('mouseout', this._onOut, true)
        window.freeWidgets = this
        this.schedule()
    },
    uninstall() {
        if (!this._installed) return
        this._installed = false
        for (const s of (this._stops || [])) s()
        this._stops = null
        window.removeEventListener('resize', this._onResize)
        document.removeEventListener('pointerup', this._onPointerUp, true)
        document.removeEventListener('mouseover', this._onOver, true)
        document.removeEventListener('mouseout', this._onOut, true)
        this._clear()
    },
    schedule(ms = 30) {
        clearTimeout(this._applyTimer)
        this._applyTimer = setTimeout(() => this.apply(), ms)
    },

    // пересчёт: кто активен, кого он перекрывает, что с ними делать
    apply() {
        const store = this.store
        if (!store) return
        const mode = this.mode()
        this.stats.applies++
        this._clear()
        try { document.body.setAttribute('data-fw-mode', mode) } catch (e) { /* нет body */ }
        if (mode === 'stack') return
        const act = store.activeWIDGET
        const WS = store.activeWORKSPACE
        const page = WS?.activePAGE?.page ?? WS?.active_page
        if (!act || act.close || act._softClosed || !page || act.page !== page || act.workspace !== WS?.workspace) return
        if (act.styleWidget?.dock === 'popout') return          // активен свободный — обычная раскладка
        const el = document.getElementById('WIDGET' + act.id)
        const r = el?.getBoundingClientRect()
        if (!r || !(r.width > 0 && r.height > 0)) return
        if (mode === 'front') {
            el.setAttribute('data-fw-front', '1')
            this._front = el
            return
        }
        const parked = []
        for (const W of (WS.ALL_WIDGETS || [])) {
            if (!W || W === act || W.page !== page || W.close || W._softClosed || W.styleWidget?.dock !== 'popout') continue
            const fel = document.getElementById('WIDGET' + W.id)
            const fr = fel?.getBoundingClientRect()
            if (!fr || !(fr.width > 0 && fr.height > 0)) continue
            if (!(fr.left < r.right && fr.right > r.left && fr.top < r.bottom && fr.bottom > r.top)) continue   // не перекрывает
            fel.setAttribute('data-fw', mode)
            if (mode === 'shade') {
                const hc = fel.querySelector('.widget-container')
                const h = hc ? Math.ceil(hc.offsetTop + hc.offsetHeight) : 40
                fel.style.setProperty('--fw-shade-h', h + 'px')
            }
            this._marked.push(fel)
            this.stats.marks++
            if (mode === 'park') parked.push(markRaw({
                id: W.id,
                title: W.title || W.shortTitle || W.record?.title || W.widget || ('#' + W.id),
                pi: W.pi || 'pi pi-table',
                W,
            }))
        }
        this.state.parked = parked
    },
    _clear() {
        this._unpeek()
        if (this._front) { this._front.removeAttribute('data-fw-front'); this._front = null }
        for (const fel of this._marked) {
            fel.removeAttribute('data-fw')
            fel.style.removeProperty('--fw-shade-h')
        }
        this._marked = []
        if (this.state.parked.length) this.state.parked = []
    },

    // peek: наведение на живую часть отступившего свободного виджета (шапка / чип) — показать целиком поверх
    onOver(e) {
        const w = e.target?.closest?.('.WIDGET[data-fw]')
        if (w) this._peek(w)
    },
    onOut(e) {
        const w = this._peekEl
        if (!w) return
        const to = e.relatedTarget
        if (to && (w.contains(to) || to.closest?.('.fw-strip-chip[data-fw-id="' + w.id + '"]'))) return
        clearTimeout(this._peekTimer)
        this._peekTimer = setTimeout(() => this._unpeek(), 250)
    },
    _peek(w) {
        clearTimeout(this._peekTimer)
        if (this._peekEl === w) return
        this._unpeek()
        w.setAttribute('data-fw-peek', '1')
        this._peekEl = w
    },
    _unpeek() {
        clearTimeout(this._peekTimer); this._peekTimer = null
        if (this._peekEl) { this._peekEl.removeAttribute('data-fw-peek'); this._peekEl = null }
    },

    // полоса парковки (FreeWidgetsStrip.vue)
    peekParked(item, on) {
        const el = item && document.getElementById('WIDGET' + item.id)
        if (!el) return
        if (on) this._peek(el)
        else { clearTimeout(this._peekTimer); this._peekTimer = setTimeout(() => this._unpeek(), 250) }
    },
    unpark(item) {
        const W = item?.W
        if (!W || !this.store || W.close) return
        this._unpeek()
        this.store.widget_activate(W, 'freeWidgets:unpark', true, true)
    },
}

export default freeWidgets
